import React from "react";

import { faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Alert, ButtonStandard, Text } from "@performyard/py-components";

import { FormQuestionPreviewCard } from "./FormQuestionPreviewCard";
import { GeneratedContentContainer } from "./GeneratedContentContainer";

import type { FormQuestionSuggestionState, FormQuestionSuggestionWithId } from "../types";

type FormQuestionSuggestionsContainerProps = {
	suggestionStates: FormQuestionSuggestionState[];
	onAddQuestion: (suggestion: FormQuestionSuggestionWithId) => void;
};

export const FormQuestionSuggestionsContainer: React.FC<FormQuestionSuggestionsContainerProps> = ({
	suggestionStates,
	onAddQuestion,
}) => {
	const visible = suggestionStates.filter((fs) => fs.status !== "deleted");
	const allDeleted = visible.length === 0;
	const isRestored = visible.some((fs) => fs.isRestored);
	const pending = visible.filter((fs) => !fs.isRestored && fs.status === "pending");
	const added = visible.filter((fs) => fs.status === "added");
	// Restored cards are pending-looking but inert, so they leave `pending` empty
	// without anything having been added. Without the guard a replayed block
	// claims the user added every question it suggested.
	const allAdded = !isRestored && visible.length > 1 && pending.length === 0;

	return (
		<GeneratedContentContainer>
			{allDeleted ? (
				<Text size="paragraph-3" color="neutral-7">
					All suggested questions have been removed.
				</Text>
			) : (
				<div className="flex flex-col gap-2">
					{visible.map((fs) => (
						<FormQuestionPreviewCard
							key={fs.suggestion.id}
							suggestion={fs.suggestion}
							status={fs.status}
							isRestored={fs.isRestored}
							onAdd={() => onAddQuestion(fs.suggestion)}
						/>
					))}
					{pending.length > 1 && (
						<ButtonStandard
							variant="solid"
							color="primary"
							size="medium"
							iconLeft={faPlus}
							onClick={() => pending.forEach((fs) => onAddQuestion(fs.suggestion))}
							data-testid="askpy-add-all-questions-button"
						>
							Add All Questions
						</ButtonStandard>
					)}
					{allAdded && (
						<Alert
							type="success"
							size="paragraph-3"
							items="center"
							message={`${added.length} ${added.length === 1 ? "Question" : "Questions"} Added`}
						/>
					)}
				</div>
			)}
		</GeneratedContentContainer>
	);
};
